import * as React from 'react';
import FindLocationInfo, { FindLocationInfoType } from './FindLocationInfo';
import FindLocationResultState from '../data/schema/FindLocationResultState';
import getLocationResultAriaLabel from '../utils/getLocationResultAriaLabel';
import LocationResult from './LocationResult';
import resolveSelectedLocation from '../actions/resolveSelectedLocation';
import showHideFindResult from '../actions/showHideFindResult';
import strings from 'owa-strings/owa-calendar-strings';
import StyledHoverElement from 'owa-component-styling-utils/lib/components/StyledHoverElement';
import { AriaRoles } from 'owa-accessibility';
import { Callout } from 'office-ui-fabric-react/lib/Callout';
import { ControlIcons } from 'owa-control-icons';
import { DirectionalHint } from 'office-ui-fabric-react/lib/common/DirectionalHint';
import { format } from 'owa-strings';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isNullOrWhiteSpace } from 'owa-string-utils';
import { LocationWellColor } from 'owa-location-common-well/lib/utils/generateLocationWellStyle';
import { observer } from 'mobx-react';
import getAddRoomIndex from '../utils/getAddRoomIndex';
import getFindResultsOffset from '../utils/getFindResultsOffset';

let styles = require('./FindLocationResult.scss');
let classNames = require('classnames/bind').bind(styles);

export interface FindLocationResultProps {
    findLocationResultState: FindLocationResultState;
    queryInputGetter: () => HTMLElement;
    className?: string;
    locationWellColor: LocationWellColor;
    showAddRoom: boolean;
    roomFreeBusyEnabled: boolean;
    addRoomHandler?: () => void;
    disableUseThisLocation?: boolean;
    requestRoomOnly?: boolean;
    dismissOnEmptyQuery?: boolean;
}

/**
 * This is locations search results drop down.
 */
@observer
export default class FindLocationResult extends React.Component<FindLocationResultProps, any> {
    private dismissFindResult = () => {
        showHideFindResult(this.props.findLocationResultState, false /**shouldShowFindResult */);

        if (
            (this.props.findLocationResultState.queryString &&
                this.props.findLocationResultState.queryString.length > 0) ||
            this.props.dismissOnEmptyQuery
        ) {
            // If we have unresolved string in the control, then resolve it when the findResult is dismissed
            resolveSelectedLocation(
                this.props.findLocationResultState,
                this.props.findLocationResultState.queryString
            );
        }
    };

    /**
     * function called when use this location is clicked
     */
    private useThisLocation = (evt: React.MouseEvent<any>) => {
        evt.preventDefault();
        resolveSelectedLocation(
            this.props.findLocationResultState,
            this.props.findLocationResultState.queryString
        );
    };

    render() {
        let { findLocationResultState, queryInputGetter } = this.props;

        if (!findLocationResultState) {
            return null;
        }

        // Initialize the narrated element
        let calloutElements = this.createFindLocationResultElements();

        // If we don't have elementa to show then we won't show the call out.
        // This case happens when we have no suggestions to show and no add room button
        if (calloutElements.length == 0) {
            showHideFindResult(findLocationResultState, false /* shouldShowFindResult */);
            return null;
        }

        return (
            findLocationResultState.shouldShowFindResult && (
                <Callout
                    id={'FindLocationResult'}
                    className={classNames(styles.callout, this.props.className)}
                    onDismiss={this.dismissFindResult}
                    calloutWidth={queryInputGetter().offsetWidth}
                    gapSpace={1}
                    target={queryInputGetter()}
                    isBeakVisible={false}
                    setInitialFocus={false}
                    directionalHint={DirectionalHint.bottomLeftEdge}>
                    {calloutElements}
                </Callout>
            )
        );
    }

    private elementHoverWrapper(
        wrappedComponent: JSX.Element,
        isHighlighted: boolean,
        key: string
    ): JSX.Element {
        const { selectedColor, selectedHoverColor } = this.props.locationWellColor;

        return (
            <StyledHoverElement
                isHighlighted={isHighlighted}
                highlightedColor={selectedColor}
                highlightedHoverColor={selectedHoverColor}
                key={key}>
                {wrappedComponent}
            </StyledHoverElement>
        );
    }

    /**
     * Makes the dom element for the find location dropdown
     */
    private createFindLocationResultElements() {
        let retElements: JSX.Element[] = [];
        const { findLocationResultState, disableUseThisLocation } = this.props;
        let hasQueryString = !isNullOrWhiteSpace(findLocationResultState.queryString);
        let hasFindResult =
            findLocationResultState.findResultSet &&
            findLocationResultState.findResultSet.length > 0;

        if (findLocationResultState.shouldShowFindResult) {
            // if user types in a location string we show "use this location" header
            if (hasQueryString && !disableUseThisLocation) {
                let useThisLocation = this.createUseThisLocationElement(findLocationResultState);
                retElements.push(
                    this.elementHoverWrapper(
                        useThisLocation,
                        findLocationResultState.findResultHightlightIndex == 0,
                        'usethislocation'
                    )
                );
            } else if (hasFindResult) {
                // if user didn't type a location we show "suggested locations" label if we have results
                retElements.push(
                    <div className={styles.findLocationHeaderBox} key="suggestedLocations">
                        <span
                            style={{
                                color: this.props.locationWellColor.rootColor,
                            }}
                            className={classNames(styles.findLocationHeaderText)}>
                            {strings.suggestedLocationsText}
                        </span>
                    </div>
                );
            }

            if (hasFindResult) {
                let locationCollection = this.createLocationCollectionElements();
                retElements.push(
                    <div
                        className={classNames(styles.scrollContainer, 'customScrollBar')}
                        key="locationResults">
                        <div className={styles.locationCollection}>{locationCollection}</div>
                    </div>
                );
            }

            // Initialize the FindLocationInfo element
            let infoElement: JSX.Element = this.createInfoElement();
            if (infoElement) {
                retElements.push(infoElement);
            }

            let narratorElement: JSX.Element = this.createAriaLiveDiv();
            if (narratorElement) {
                retElements.push(narratorElement);
            }

            // Hide 'Add Room' button if cal-mf-hideAddRoomForZeroQuery feature is enabled and query string is empty.
            // if cal-mf-hideAddRoomForZeroQuery feature is disabled, keep the behavior unchanged.
            if (
                this.props.showAddRoom &&
                (!isFeatureEnabled('cal-mf-hideAddRoomForZeroQuery') || hasQueryString)
            ) {
                let addRoomItem = this.getRoomItem();
                retElements.push(
                    this.elementHoverWrapper(
                        addRoomItem,
                        findLocationResultState.findResultHightlightIndex ==
                            getAddRoomIndex(findLocationResultState, disableUseThisLocation),
                        'browsemoreplaces'
                    )
                );
            }
        }

        return retElements;
    }

    /**
     * Makes the div for use this location
     */
    private createUseThisLocationElement(findLocationResultState: FindLocationResultState) {
        return (
            <div
                className={classNames(styles.findLocationHeaderBox, styles.useThisLocationHeader)}
                onMouseUp={this.useThisLocation}
                key="useThisLocation">
                <span className={styles.findLocationHeaderText}>
                    {this.getUseThisLocationText(findLocationResultState.queryString)}
                </span>
            </div>
        );
    }

    private getRoomItem() {
        return (
            <div
                className={styles.roomButtonContainer}
                onMouseUp={this.switchToRoomList}
                key="roomItem">
                <div className={styles.addRoomIcon}>
                    <Icon iconName={ControlIcons.Add} className={styles.locationRowIcon} />
                </div>
                <div className={styles.addRoomText}>
                    <span>
                        {isFeatureEnabled('cal-advancedRoomBooking')
                            ? strings.browseMorePlacesTitle
                            : strings.addroomText}
                    </span>
                </div>
            </div>
        );
    }

    private switchToRoomList = () => {
        //Hide drop down
        showHideFindResult(this.props.findLocationResultState, false /**shouldShowFindResult */);

        //call the add room handler
        this.props.addRoomHandler();
    };

    /**
     * builds the use this location string
     */
    private getUseThisLocationText(queryString: string) {
        return format(strings.useThisLocationText, queryString);
    }

    /**
     * Makes the div for use this location
     */
    private createLocationCollectionElements() {
        const { findLocationResultState, disableUseThisLocation } = this.props;
        const findResultsOffset = getFindResultsOffset(
            findLocationResultState,
            disableUseThisLocation
        );
        return findLocationResultState.findResultSet.map((result, index) => {
            let locationResult = (
                <LocationResult
                    findLocationResultState={findLocationResultState}
                    locationResult={result}
                    locationWellColor={this.props.locationWellColor}
                    roomFreeBusyEnabled={this.props.roomFreeBusyEnabled}
                />
            );
            return this.elementHoverWrapper(
                locationResult,
                index == findLocationResultState.findResultHightlightIndex - findResultsOffset,
                result.personaControlId
            );
        }, this);
    }

    /**
     * Makes the div at the bottom of the
     */
    private createInfoElement() {
        const { findLocationResultState } = this.props;

        // Initialize the FindLocationInfo element
        let infoElement: JSX.Element = null;

        let hasQueryString = !isNullOrWhiteSpace(findLocationResultState.queryString);
        let hasFindResult =
            findLocationResultState.findResultSet &&
            findLocationResultState.findResultSet.length > 0;

        if (findLocationResultState.isSearching) {
            // if searching for suggestions show "loading", if searching for a specific place show "searching for 'the place' "
            infoElement = hasQueryString ? (
                <FindLocationInfo
                    infoType={FindLocationInfoType.Searching}
                    queryString={findLocationResultState.queryString.trim()}
                    key="findLocationInfo_Searching"
                />
            ) : (
                <FindLocationInfo
                    infoType={FindLocationInfoType.Loading}
                    key="findLocationInfo_Loading"
                />
            );
        } else if (hasQueryString && !hasFindResult) {
            // if we searched for a location but no results were found show "No results found"
            infoElement = (
                <FindLocationInfo
                    infoType={FindLocationInfoType.NoResults}
                    key="findLocationInfo_NoResults"
                />
            );
        }

        return infoElement;
    }

    /**
     * Makes the div for the Aria Live region needed for narrating the charm suggestion items.
     */
    private createAriaLiveDiv() {
        const { findLocationResultState, disableUseThisLocation } = this.props;
        const findResultsOffset = getFindResultsOffset(
            findLocationResultState,
            disableUseThisLocation
        );
        let ariaLiveMessage = '';
        let hasFindResult =
            findLocationResultState.findResultSet &&
            findLocationResultState.findResultSet.length > 0;

        // If we show the drop down with some results and no location is highlighted, then inform the user of the suggestions,
        // Otherwise we read the highlighted location
        if (findLocationResultState.findResultHightlightIndex < 0 && hasFindResult) {
            ariaLiveMessage = strings.multipleLocationsFoundAriaLabel;
        } else if (findLocationResultState.findResultHightlightIndex >= 0) {
            // if the highlighted section is use this location
            if (findLocationResultState.findResultHightlightIndex < findResultsOffset) {
                ariaLiveMessage = this.getUseThisLocationText(findLocationResultState.queryString);
            } else {
                // the highlight is on a location from the result
                let highlightedLocation =
                    findLocationResultState.findResultSet[
                        findLocationResultState.findResultHightlightIndex - findResultsOffset
                    ];
                if (highlightedLocation) {
                    ariaLiveMessage = getLocationResultAriaLabel(
                        highlightedLocation,
                        this.props.roomFreeBusyEnabled
                    );
                }
            }
        }

        if (!isNullOrWhiteSpace(ariaLiveMessage)) {
            return (
                <div
                    className={styles.narratorOnly}
                    role={AriaRoles.listitem}
                    aria-live="polite"
                    aria-atomic="true"
                    key="selectedSuggestion">
                    {ariaLiveMessage}
                </div>
            );
        }

        return null;
    }
}
